import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  inject,
  input,
  linkedSignal,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { StorageService } from '../../services/storage/storage.service';
import { UserService } from '../../services/user/user.service';
import { SupportAlertPreferences } from '../../common/general-types/support-ticket';
import { isUkSupportCountry } from '../../common/support-chat.util';

@Component({
  selector: 'app-support-alert-preferences-input',
  imports: [FormsModule],
  templateUrl: './support-alert-preferences-input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block' },
})
export class SupportAlertPreferencesInputComponent implements OnInit, OnDestroy {
  readonly alertPreferences = input.required<SupportAlertPreferences>();
  readonly alertPreferencesChange = output<SupportAlertPreferences>();
  readonly mobileSaved = output<string>();
  readonly allowMobileUpdate = input(true);
  readonly userMobile = input<string | null>(null);
  readonly userEmail = input<string | null>(null);
  /** Only the mobile apps register a device, so most people on the web can't be pushed to. */
  readonly hasPushDevice = input(false);

  readonly currentMobile = signal('');
  readonly currentEmail = signal('');
  readonly mobileInput = signal('');
  readonly savingMobile = signal(false);
  readonly mobileError = signal('');
  readonly mobileSuccess = signal('');

  readonly prefs = linkedSignal(() => ({ ...this.alertPreferences() }));

  private readonly subscriptionList = new Subscription();
  private readonly storageService = inject(StorageService);
  private readonly userService = inject(UserService);
  private readonly currentUserService = inject(CurrentUserService);

  ngOnInit(): void {
    this.syncContactDetails();
    this.ensureValidPreferences();
    this.refreshMobileFromAccount();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  hasMobile(): boolean {
    return !!this.currentMobile().trim();
  }

  hasEmail(): boolean {
    return !!this.currentEmail().trim();
  }

  onSmsChange(checked: boolean): void {
    if (!this.hasMobile()) {
      return;
    }
    this.emitPrefs({ ...this.prefs(), sms: checked });
  }

  onWhatsappChange(checked: boolean): void {
    if (!this.hasMobile()) {
      return;
    }
    this.emitPrefs({ ...this.prefs(), whatsapp: checked });
  }

  onEmailChange(checked: boolean): void {
    this.emitPrefs({ ...this.prefs(), email: checked });
  }

  onPushChange(checked: boolean): void {
    if (!this.hasPushDevice()) {
      return;
    }
    this.emitPrefs({ ...this.prefs(), push: checked });
  }

  saveMobile(): void {
    const mobile = this.mobileInput().trim();
    if (!mobile) {
      this.mobileError.set('Enter a mobile number.');
      return;
    }

    const userId =
      this.storageService.firebaseUser?.uid ??
      this.currentUserService.shoutUser?.firebaseUid;
    if (!userId) {
      this.mobileError.set('Could not save your number. Please try again from your account settings.');
      return;
    }

    this.savingMobile.set(true);
    this.mobileError.set('');
    this.mobileSuccess.set('');

    this.subscriptionList.add(
      this.userService.updateUser({ mobileNumber: mobile }, userId).subscribe({
        next: (updatedUser) => {
          const nextMobile =
            (updatedUser as { mobileNumber?: string })?.mobileNumber?.trim() || mobile;
          this.applySavedMobile(nextMobile, updatedUser);
          this.savingMobile.set(false);
          this.mobileSuccess.set('Mobile number saved.');
        },
        error: () => {
          this.savingMobile.set(false);
          this.mobileError.set('Could not save your mobile number. Please try again.');
        },
      }),
    );
  }

  private applySavedMobile(nextMobile: string, updatedUser?: unknown): void {
    this.currentMobile.set(nextMobile);
    this.mobileInput.set(nextMobile);
    this.persistMobileOnCurrentUser(nextMobile, updatedUser);
    this.mobileSaved.emit(nextMobile);
    this.enableMobileAlertPreferences();
  }

  private persistMobileOnCurrentUser(nextMobile: string, updatedUser?: unknown): void {
    const shoutUser = this.currentUserService.shoutUser;
    if (!shoutUser && !updatedUser) {
      return;
    }

    const nextUser = {
      ...(shoutUser ?? {}),
      ...((updatedUser as Record<string, unknown> | undefined) ?? {}),
      mobileNumber: nextMobile,
    };
    this.currentUserService.setShoutUser(nextUser);
  }

  private enableMobileAlertPreferences(): void {
    const countryCode =
      this.currentUserService.shoutUser?.countryCode ??
      this.currentUserService.focussedBrand?.countryCode;
    const isUk = isUkSupportCountry(countryCode);
    const next = { ...this.prefs() };
    let changed = false;

    if (isUk) {
      if (!next.whatsapp) {
        next.whatsapp = true;
        changed = true;
      }
    } else if (!next.sms) {
      next.sms = true;
      changed = true;
    }

    if (changed) {
      this.emitPrefs(next);
    }
  }

  private refreshMobileFromAccount(): void {
    if (this.hasMobile() || !this.allowMobileUpdate()) {
      return;
    }

    const userId =
      this.storageService.firebaseUser?.uid ??
      this.currentUserService.shoutUser?.firebaseUid;
    if (!userId) {
      return;
    }

    this.subscriptionList.add(
      this.userService.getUser(userId).subscribe({
        next: (user) => {
          const nextMobile = (user as { mobileNumber?: string })?.mobileNumber?.trim();
          if (!nextMobile) {
            return;
          }
          this.currentMobile.set(nextMobile);
          this.mobileInput.set(nextMobile);
          this.persistMobileOnCurrentUser(nextMobile, user);
          this.enableMobileAlertPreferences();
        },
      }),
    );
  }

  private emitPrefs(next: SupportAlertPreferences): void {
    this.prefs.set(next);
    this.alertPreferencesChange.emit(next);
  }

  private syncContactDetails(): void {
    const shoutUser = this.currentUserService.shoutUser;
    this.currentMobile.set(
      this.userMobile()?.trim() ||
        shoutUser?.mobileNumber?.trim() ||
        '',
    );
    this.currentEmail.set(
      this.userEmail()?.trim() ||
        shoutUser?.email?.trim() ||
        '',
    );

    if (!this.mobileInput().trim() && this.currentMobile()) {
      this.mobileInput.set(this.currentMobile());
    }
  }

  private ensureValidPreferences(): void {
    this.syncContactDetails();
    const next = { ...this.prefs() };
    let changed = false;

    if (!this.hasMobile()) {
      if (next.sms) {
        next.sms = false;
        changed = true;
      }
      if (next.whatsapp) {
        next.whatsapp = false;
        changed = true;
      }
    }

    if (!this.hasPushDevice() && next.push) {
      next.push = false;
      changed = true;
    }

    if (changed) {
      this.emitPrefs(next);
    }
  }
}
